import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useRecoilState, useResetRecoilState } from 'recoil';
import styled from 'styled-components';

import { Wizard } from 'src/app/components/lib/wizard';
import { useActivateAutomatedMarketingCampaignMutation } from 'src/app/queries/marketing/activate-automated-campaign';
import { useGetWorkflowCampaignQuery } from 'src/app/queries/marketing/get-workflow-campaign';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';

import { formatTimeAgo } from '../../../campaigns/marketing-campaign-builder/helpers';
import { WorkflowCampaignBasicInfo } from '../slides/campaign-basic-info';
import { WorkflowEditor } from '../slides/workflow-editor';
import {
  emailEditorNavigationState,
  isEmailEditorEnabled,
} from '../slides/workflow-editor/atoms/workflow-and-email-editor-atoms';

import { useWorkflowBuilderContext } from './builder-context';
import { getSaveWorkflowCampaignPayload } from './helpers';

import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

const workflowSteps = [
  {
    title: 'Details',
    Component: WorkflowCampaignBasicInfo,
    shouldFill: true,
    hideBackButton: true,
  },
  {
    title: 'Build',
    Component: WorkflowEditor,
    shouldFill: true,
  },
];

type WorkflowCampaignWizardProps = {
  onClose: () => void;
  open: boolean;
};

export function WorkflowCampaignWizard({ open, onClose }: WorkflowCampaignWizardProps) {
  const confirmModal = useModal(Modal.confirm);
  const notifications = useNotificationActions();
  const params = useParams()?.['*'];
  const isEdit = useMemo(() => params?.includes('edit'), [params]);
  const campaignIdFromURL = !isEdit ? null : Number(params?.split('/').pop());

  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { campaignName, lastModifiedDateUTC } = builderState;
  const [navState] = useRecoilState(emailEditorNavigationState);
  const resetNavState = useResetRecoilState(emailEditorNavigationState);
  const [, setShowEditor] = useRecoilState(isEmailEditorEnabled);
  const { isEditorLoading } = navState;

  const { data: workflowCampaign, isLoading: workflowDataIsLoading } = useGetWorkflowCampaignQuery({
    marketingCampaignId: campaignIdFromURL,
  });
  const { mutateAsync: saveWorkflowCampaign } = useSaveWorkflowCampaignMutation();
  const { mutateAsync: activateCampaign } = useActivateAutomatedMarketingCampaignMutation();

  useEffect(() => {
    if (workflowCampaign) {
      dispatch({ type: 'update-full-campaign', payload: workflowCampaign });
    }
  }, [workflowCampaign, dispatch]);

  const handleClose = useCallback(async () => {
    if (!isEdit) {
      resetNavState();
      setShowEditor(false);
      onClose();
      return;
    }

    try {
      await saveWorkflowCampaign(getSaveWorkflowCampaignPayload({}, builderState));

      notifications.success({
        message: 'Workflow saved',
        subContent: 'Your workflow has been saved successfully.',
      });

      resetNavState();
      setShowEditor(false);
      onClose();
    } catch (e) {
      const error = e as ApiErrorResponse;
      notifications.error({
        message: error?.response?.data?.Message ?? 'Error saving workflow',
      });
    }
  }, [isEdit, setShowEditor, onClose, saveWorkflowCampaign, builderState, resetNavState]);

  const handleActivate = useCallback(async () => {
    confirmModal.openModal({
      onConfirm: async () => {
        let campaignId = builderState.marketingCampaignId;
        try {
          const response = await saveWorkflowCampaign(
            getSaveWorkflowCampaignPayload({ ScheduleSendForUtc: new Date().toISOString() }, builderState)
          );
          campaignId = response.MarketingCampaignId;
        } catch (e) {
          const error = e as ApiErrorResponse;
          notifications.error({
            message: error?.response?.data?.Message ?? 'Error saving workflow',
          });
          return;
        }

        try {
          await activateCampaign({
            MarketingCampaignId: campaignId,
          });
        } catch (e) {
          const error = e as ApiErrorResponse;
          notifications.error({
            message: error?.response?.data?.Message ?? 'Error activating workflow',
          });
          return;
        }

        notifications.success({
          message: 'Workflow activated',
          subContent: 'Your workflow has been activated successfully.',
        });

        resetNavState();
        setShowEditor(false);
        onClose();
      },
      title: 'Create automated campaign?',
      helpText:
        'This will activate your automated campaign. Messages will be automatically sent to consumers that trigger these events.',
    });
  }, [confirmModal, builderState, onClose, saveWorkflowCampaign, activateCampaign, resetNavState, setShowEditor]);

  return (
    <Wizard
      completeButtonLabel='Activate workflow'
      drawerAriaLabel='Create workflow wizard'
      exitButtonLabel={isEdit ? 'Save & exit' : 'Exit'}
      loading={isEditorLoading && workflowDataIsLoading}
      open={open}
      steps={workflowSteps}
      subtitle={<Subtitle isLoading={workflowDataIsLoading} lastSavedAt={lastModifiedDateUTC} />}
      title={isEdit ? campaignName : 'Create workflow'}
      titleFontSize='var(--font-large-16pt-semibold)'
      titleMinWidth='300px'
      onClose={handleClose}
      onCompleteButtonClick={handleActivate}
    />
  );
}

function Subtitle({ lastSavedAt, isLoading }) {
  const [formattedLastSavedAt, setFormattedLastSavedAt] = useState<string>(formatTimeAgo(lastSavedAt));

  // recheck the formatted string after 16 sec (to handle update from "a few seconds ago" to the full timestamp)
  useEffect(() => {
    setFormattedLastSavedAt(formatTimeAgo(lastSavedAt));

    const timeoutID = setTimeout(() => {
      setFormattedLastSavedAt(formatTimeAgo(lastSavedAt));
    }, 16000);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [lastSavedAt]);

  const shouldShowTimestamp = !isLoading && lastSavedAt;
  const noTimestampText = isLoading ? '--' : 'Unsaved';

  return (
    <div>
      {shouldShowTimestamp ? (
        <>
          <StyledLightType>Last saved: </StyledLightType>
          <StyledDarkerType>{formattedLastSavedAt}</StyledDarkerType>
        </>
      ) : (
        <StyledDarkerType>{noTimestampText}</StyledDarkerType>
      )}
    </div>
  );
}

const StyledLightType = styled.span`
  font: var(--font-extra-small-12pt-normal);
  color: var(--color-gray-80);
`;
const StyledDarkerType = styled.span`
  font: var(--font-extra-small-12pt-normal);
  color: var(--color-gray-90);
`;
